import { useState } from "react";
// outsource
import { Modal, Divider } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
// functions
import {
  formatPayoutDate,
  formatShiftDay,
  formatShiftTime,
} from "../functions/payout_list_format";
import {
  normalizeIncludeXeroHours,
  PAYOUT_HOLIDAY_HOURS_MULTIPLIER,
  payoutNumberOrZero,
} from "../functions/payout_list_calculation";

const CATEGORY_LABEL = {
  XRP: "XERO Profile (XRP)",
  NXRP: "Cash Profile (NXRP)",
};

const PayoutCardDialog = ({
  viewingStaff,
  setViewingStaff,
  formatMoney,
  formatHours,
  payPeriod = "28 Dec 2025 - 03 Jan 2026",
  afterHourRate = 1,
  holidayRate = 1,
}) => {
  // -------------------------- STATE --------------------------
  const [collapsedShops, setCollapsedShops] = useState([]);

  // -------------------------- VAR --------------------------
  if (!viewingStaff) return null;

  const money = (value) => {
    const num = payoutNumberOrZero(value);
    const formatted = formatMoney(Math.abs(num));
    return num < 0 ? `-$${formatted}` : `$${formatted}`;
  };
  const hours = (value) => formatHours(payoutNumberOrZero(value));
  const rate = (value) =>
    payoutNumberOrZero(value).toLocaleString("en-NZ", {
      maximumFractionDigits: 2,
    });
  const effectiveAfterHourRate = payoutNumberOrZero(afterHourRate);
  const effectiveHolidayRate = payoutNumberOrZero(holidayRate);

  const category = String(viewingStaff.category || "").toUpperCase();
  const includeXeroHours = normalizeIncludeXeroHours(
    viewingStaff.includeXeroHours,
  );
  const isXeroProfile = category === "XRP";
  const isXeroIncludeHours = isXeroProfile && includeXeroHours;
  const isXeroExcludeHours = isXeroProfile && !includeXeroHours;
  const payrate = payoutNumberOrZero(
    viewingStaff.nonXeroPayrate ?? viewingStaff.payrate,
  );
  const regularHrs = payoutNumberOrZero(viewingStaff.regularHrs);
  const afterHrs = payoutNumberOrZero(viewingStaff.afterHrs);
  const holidayHrs = payoutNumberOrZero(viewingStaff.holidayHrs);
  const bonus = payoutNumberOrZero(viewingStaff.bonus);
  const fuelSupport = payoutNumberOrZero(viewingStaff.fuelSupport);
  const kiwiSaver = payoutNumberOrZero(
    viewingStaff.kiwiSaver ?? viewingStaff.kiwisaver,
  );
  const xeroPayrate = payoutNumberOrZero(viewingStaff.xeroPayrate);
  const xeroHours = payoutNumberOrZero(viewingStaff.xeroHours);
  const xeroPay = payoutNumberOrZero(viewingStaff.xeroPay);
  const totalHrs = payoutNumberOrZero(
    viewingStaff.totalHrs ??
      regularHrs +
        afterHrs +
        holidayHrs * PAYOUT_HOLIDAY_HOURS_MULTIPLIER,
  );
  const baseHours = isXeroExcludeHours
    ? totalHrs
    : isXeroIncludeHours
      ? totalHrs - xeroHours
      : regularHrs;
  const regularHoursFormula = isXeroIncludeHours
    ? `(${hours(totalHrs)} hrs - ${hours(xeroHours)} hrs) × ${money(payrate)}/hr`
    : `(${hours(baseHours)} hrs × ${money(payrate)}/hr)`;
  const basePay = baseHours * payrate;
  const afterHoursAmount = afterHrs * effectiveAfterHourRate * payrate;
  const holidayAmount = holidayHrs * effectiveHolidayRate * payrate;
  const shops = Array.isArray(viewingStaff.shops) ? viewingStaff.shops : [];
  const cash = payoutNumberOrZero(viewingStaff.cash);
  const bank = payoutNumberOrZero(viewingStaff.bank);
  const totalPayout = payoutNumberOrZero(viewingStaff.totalPayout);

  // -------------------------- RENDER --------------------------
  const renderInfoRow = (label, value, bold = false) => (
    <div className="payout-info-row">
      <div className="payout-info-label">{label}</div>
      <div className={`payout-info-value${bold ? " payout-info-bold" : ""}`}>
        {value}
      </div>
    </div>
  );

  const renderSummaryRow = (label, value, className = "") => (
    <div className={`payout-summary-row ${className}`}>
      <div>{label}</div>
      <strong>{value}</strong>
    </div>
  );

  const toggleShop = (branchId) =>
    setCollapsedShops((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId],
    );

  // Level 1: shop code + week total. Level 2: the shifts behind that total.
  const renderShopBreakdown = () => {
    if (!shops.length) return null;

    return (
      <div className="payout-shop-list">
        {shops.map((shop) => {
          const collapsed = collapsedShops.includes(shop.branchId);
          return (
            <div className="payout-shop" key={shop.branchId}>
              <div
                className="payout-shop-row"
                role="button"
                tabIndex={0}
                onClick={() => toggleShop(shop.branchId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleShop(shop.branchId);
                  }
                }}
              >
                {collapsed ? <DownOutlined /> : <UpOutlined />}
                <span>
                  {shop.code} ({hours(shop.hours)} hrs)
                </span>
              </div>
              {!collapsed && (
                <div className="payout-shift-table">
                  {shop.days.map((day) => (
                    <div
                      className="payout-shift-row"
                      key={`${shop.branchId}-${day.date}`}
                    >
                      <div>{formatShiftDay(day.date)}</div>
                      <div>{formatShiftTime(day.checkInAt)}</div>
                      <div>{formatShiftTime(day.checkOutAt)}</div>
                      <div>{hours(day.hours)} hrs</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummaryBox = () => (
    <div className="payout-summary-box">
      <div className="payout-summary-title">Payout summary</div>
      <div className="payout-summary-row">
        <div>
          Regular Hours{" "}
          <span>{regularHoursFormula}</span>
        </div>
        <strong>{money(basePay)}</strong>
      </div>
      {renderShopBreakdown()}
      <div className="payout-summary-row">
        <div>
          After-Hours{" "}
          <span>
            ({hours(afterHrs)} hrs × {rate(effectiveAfterHourRate)} ×{" "}
            {money(payrate)}/hr)
          </span>
        </div>
        <strong>{money(afterHoursAmount)}</strong>
      </div>
      <div className="payout-summary-row">
        <div>
          Holiday Hours{" "}
          <span>
            ({hours(holidayHrs)} hrs × {rate(effectiveHolidayRate)} ×{" "}
            {money(payrate)}/hr)
          </span>
        </div>
        <strong>{money(holidayAmount)}</strong>
      </div>
      {renderSummaryRow("Bonus", money(bonus))}
      {fuelSupport > 0 && renderSummaryRow("Fuel Support", money(fuelSupport))}
      {kiwiSaver > 0 && renderSummaryRow("KiwiSaver", money(-kiwiSaver))}
      <Divider className="payout-divider" />
      {renderSummaryRow("Total Payout", money(totalPayout), "payout-total-row")}
      {renderSummaryRow(". Cash", money(cash), "payout-sub-row")}
      {renderSummaryRow(". Bank", money(bank), "payout-sub-row")}
    </div>
  );

  const renderSettingsSection = () => (
    <>
      <Divider className="payout-section-divider" />
      <div className="payout-settings-title">Payout Settings</div>
      <div className="payout-setting-row">
        <div>Payout Category</div>
        <span>
          {CATEGORY_LABEL[category] || category}
        </span>
      </div>
      {category === "XRP" && (
        <div className="payout-setting-row">
          <div>Include XERO Hours?</div>
          <span>{includeXeroHours ? "Yes" : "No"}</span>
        </div>
      )}
      <div className="payout-setting-row">
        <div>After-Hour Rate</div>
        <span>{rate(effectiveAfterHourRate)}x</span>
      </div>
      <div className="payout-setting-row">
        <div>Holiday Rate</div>
        <span>{rate(effectiveHolidayRate)}x</span>
      </div>
      {isXeroProfile && (
        <>
          <div className="payout-setting-row">
            <div>XERO Payrate</div>
            <span>{money(xeroPayrate)}/hr</span>
          </div>
          <div className="payout-setting-row">
            <div>XERO Hours</div>
            <span>{hours(xeroHours)} hrs/week</span>
          </div>
          {isXeroExcludeHours && (
            <div className="payout-setting-row">
              <div>XERO Pay</div>
              <span>{money(xeroPay)}</span>
            </div>
          )}
        </>
      )}
      <div className="payout-setting-row">
        <div>Non-XERO Payrate</div>
        <span>{money(viewingStaff.nonXeroPayrate ?? payrate)}/hr</span>
      </div>
    </>
  );

  const renderBody = () => (
    <div className="payout-card">
      <div className="payout-card-header">
        <h3 className="payout-card-title">{viewingStaff.fullName}</h3>
      </div>
      {renderInfoRow("Pay period", payPeriod)}
      {renderSummaryBox()}
      <div className="payout-info-row payout-payment-date">
        <div className="payout-info-label">Payment Date:</div>
        <div className="payout-info-value">
          {formatPayoutDate(viewingStaff?.paidTime)}
        </div>
      </div>
      {renderSettingsSection()}
    </div>
  );

  // -------------------------- MAIN --------------------------
  return (
    <Modal
      open={!!viewingStaff}
      title={null}
      footer={null}
      onCancel={() => setViewingStaff(null)}
      width={500}
      centered
      style={{ paddingBottom: 0 }}
      className="payout-card-modal"
    >
      {renderBody()}
    </Modal>
  );
};

export default PayoutCardDialog;
